import { FileText, Image, Mail } from '../../ui/Icon';

const R2_PUBLIC_URL = (import.meta.env.VITE_R2_PUBLIC_URL as string | undefined)?.replace(/\/$/, '') ?? '';

/** Public URL of a stored attachment, from the R2 object key we persist as `filename`. */
export function fileUrl(filename: string): string {
  return `${R2_PUBLIC_URL}/${filename}`;
}

/** The server normalizes a saved email to `message/rfc822`, whatever the browser called it. */
export const EMAIL_MIME = 'message/rfc822';

/**
 * What a booking accepts as an attachment. Shared so the form's picker and the
 * "fill in with AI" picker can't drift — they feed the same endpoints.
 */
export const ATTACHMENT_ACCEPT = `image/*,application/pdf,${EMAIL_MIME},.eml`;

export function fileIcon(mimeType: string) {
  if (mimeType === EMAIL_MIME) return Mail;
  return mimeType.startsWith('image/') ? Image : FileText;
}

/**
 * Same icon, for a file the browser just handed us. Its `type` is empty or
 * `application/octet-stream` for a saved email — the server corrects that on
 * upload, but a staged file hasn't been there yet.
 */
export function stagedFileIcon(file: File) {
  return fileIcon(file.name.toLowerCase().endsWith('.eml') ? EMAIL_MIME : file.type);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Shape shared by FlightDocument / HotelDocument / BookingDocument / NoteDocument. */
export interface StoredDocument {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
}

/**
 * Field label styling for the booking forms. Deliberately not the global
 * `.label` class, which is uppercase + tracked — these forms use a denser,
 * sentence-case label. Shared so the three forms can't drift apart.
 */
export const FIELD_LABEL = 'block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1';
